import React, { useState, useEffect, useRef } from 'react';
import { Button, FormControl, Dropdown, MenuItem } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TFunction } from 'i18next';
import { svgIcons } from '../../constants/svgIcons';
import FilterDropdown from './FilterDropdown';
import Toggle from 'react-toggle';
import ExportToExcelButton, {
    ExportRequest
} from '../common/ExportToExcelButton';

interface INewInventorySearchProps {
    t: TFunction;
    colorButton: string;
    onSearchChange?: (value: string) => void;
    onCategoryChange?: (value: string) => void;
    onStatusChange?: (value: string) => void;
    onSearchValueChanges?: (value: string, type: string) => void;

    onToggleChange?: (value: boolean) => void;
    canEditInstalls?: boolean;
    isManageAssetView?: boolean;
    onImportClick?: () => void;
    onNewProductClick?: () => void;
    onBackClick?: () => void;
    loading?: boolean;
    categoryOptions?: Array<{ value: string; label: string }>;
    statusOptions?: Array<{ value: string; label: string }>;
    currentSearch?: string;
    currentCategory?: { value: string; label: string } | null;
    currentStatus?: { value: string; label: string } | null;
    hideDecommissionedAssets?: boolean;
    onHideAssetsToggle?: (value: boolean) => void;
    /** When provided, adds "Export to CSV/Excel" items to the actions menu. */
    getExportRequest?: () => ExportRequest;
}

const NewInventorySearch: React.FC<INewInventorySearchProps> = ({
    t,
    colorButton,
    onSearchChange,
    onCategoryChange,
    onStatusChange,
    onSearchValueChanges,

    onToggleChange,
    canEditInstalls = false,
    isManageAssetView = false,
    onImportClick,
    onNewProductClick,
    onBackClick,
    loading = false,
    categoryOptions = [],
    statusOptions = [],
    currentSearch = '',
    currentCategory = null,
    currentStatus = null,
    hideDecommissionedAssets = false,
    onHideAssetsToggle,
    getExportRequest
}) => {
    const [searchValue, setSearchValue] = useState(currentSearch);
    const [category, setCategory] = useState(currentCategory?.value || 'All');
    const [status, setStatus] = useState(currentStatus?.value || 'All');
    const [dropdownAlignRight, setDropdownAlignRight] = useState(false);
    const actionsDropdownRef = useRef<HTMLDivElement>(null);

    // Use refs to track previous prop values to avoid infinite loops
    const prevSearchRef = useRef(currentSearch);
    const prevCategoryRef = useRef(currentCategory?.value);
    const prevStatusRef = useRef(currentStatus?.value);

    // Sync local state with props when they change (e.g., from Redux state updates)
    useEffect(() => {
        if (currentSearch !== prevSearchRef.current) {
            setSearchValue(currentSearch);
            prevSearchRef.current = currentSearch;
        }
    }, [currentSearch]);

    useEffect(() => {
        const categoryValue = currentCategory?.value || 'All';
        if (categoryValue !== prevCategoryRef.current) {
            setCategory(categoryValue);
            prevCategoryRef.current = categoryValue;
        }
        // eslint-disable-next-line
    // eslint-disable-next-line
    }, [currentCategory?.value]);

    useEffect(() => {
        const statusValue = currentStatus?.value || 'All';
        if (statusValue !== prevStatusRef.current) {
            setStatus(statusValue);
            prevStatusRef.current = statusValue;
        }
        // eslint-disable-next-line
    // eslint-disable-next-line
    }, [currentStatus?.value]);

    const handleSearchChange = (e: any) => {
        const value = e.target.value;
        setSearchValue(value);
        // Use centralized callback like SearchTableForm
        onSearchValueChanges?.(value, 'search');
        // Keep backward compatibility
        onSearchChange?.(value);
    };

    const handleFilterChange = (filterType: string, value: string) => {
        switch (filterType) {
            case 'category':
                setCategory(value);
                // Use centralized callback like SearchTableForm
                onSearchValueChanges?.(value, 'mainCategory');
                // Keep backward compatibility
                onCategoryChange?.(value);
                break;
            case 'status':
                setStatus(value);
                // Use centralized callback like SearchTableForm
                onSearchValueChanges?.(value, 'status');
                // Keep backward compatibility
                onStatusChange?.(value);
                break;
        }
    };

    const handleClearFilters = () => {
        setSearchValue('');
        setCategory('All');
        setStatus('All');
        onSearchValueChanges?.('', 'search');
        onSearchValueChanges?.('All', 'mainCategory');
        onSearchValueChanges?.('All', 'status');
    };

    const handleActionSelect = (eventKey: any) => {
        switch (eventKey) {
            case 'import':
                onImportClick?.();
                break;
            case 'new-asset':
                onNewProductClick?.();
                break;
        }
    };

    const handleHideAssetsToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        onHideAssetsToggle?.(e.target.checked);
    };

    const handleDropdownToggle = (isOpen: boolean) => {
        if (isOpen && actionsDropdownRef.current) {
            const button = actionsDropdownRef.current.querySelector(
                '.actions-button'
            );
            if (button) {
                const rect = button.getBoundingClientRect();
                const dropdownWidth = 320; // max-width from CSS
                const shouldAlignRight =
                    rect.left + dropdownWidth > window.innerWidth;
                setDropdownAlignRight(shouldAlignRight);
            }
        }
    };

    return (
        <div className="inventory-search-form">
            <div className="search-bar-container">
                {/* Back Button */}
                <Button className="back-button" onClick={onBackClick}>
                    <FontAwesomeIcon icon={['fas', 'chevron-left']} />
                </Button>

                {/* Search Input */}
                <div className="search-input-container">
                    <span className="search-icon">{svgIcons.search}</span>
                    <FormControl
                        type="text"
                        placeholder="Search for Product/Asset..."
                        value={searchValue}
                        onChange={handleSearchChange}
                    />
                </div>

                {/* Filter Dropdowns */}
                <FilterDropdown
                    value={category}
                    options={[
                        { value: 'All', label: 'Category: All' },
                        ...categoryOptions
                    ]}
                    onChange={value => handleFilterChange('category', value)}
                    placeholder="Category: All"
                    defaultValue="All"
                    disabled={loading}
                />

                <FilterDropdown
                    value={status}
                    options={[
                        { value: 'All', label: 'Status: All' },
                        ...statusOptions
                    ]}
                    onChange={value => handleFilterChange('status', value)}
                    placeholder="Status: All"
                    defaultValue="All"
                    disabled={loading}
                />

                {/* Clear Button - only show when filters are active */}
                {(searchValue !== '' ||
                    category !== 'All' ||
                    status !== 'All') && (
                    <Button
                        className="clear-filters-button"
                        onClick={handleClearFilters}
                        disabled={loading}
                    >
                        <span>Clear</span>
                        <FontAwesomeIcon icon={['far', 'times']} />
                    </Button>
                )}

                {/* Actions Dropdown */}
                <div ref={actionsDropdownRef}>
                    <Dropdown
                        onSelect={handleActionSelect}
                        onToggle={handleDropdownToggle}
                        className="filter-dropdown actions-dropdown"
                        id="actions-dropdown"
                    >
                        <Dropdown.Toggle noCaret className="actions-button">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="12"
                                viewBox="0 0 14 12"
                                fill="none"
                            >
                                <path
                                    d="M1.53809 4.66699C2.38774 4.66699 3.07712 5.35543 3.07715 6.20508C3.07715 7.05475 2.38775 7.74316 1.53809 7.74316C0.688515 7.74305 0 7.05468 0 6.20508C2.71552e-05 5.3555 0.688532 4.66711 1.53809 4.66699ZM6.66699 4.66699C7.51642 4.66725 8.20505 5.35559 8.20508 6.20508C8.20508 7.05459 7.51644 7.74291 6.66699 7.74316C5.81732 7.74316 5.12793 7.05475 5.12793 6.20508C5.12796 5.35543 5.81734 4.66699 6.66699 4.66699ZM11.7949 4.66699C12.6445 4.6671 13.333 5.3555 13.333 6.20508C13.333 7.05468 12.6445 7.74306 11.7949 7.74316C10.9453 7.74316 10.2559 7.05475 10.2559 6.20508C10.2559 5.35543 10.9453 4.66699 11.7949 4.66699Z"
                                    fill="white"
                                />
                            </svg>
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                            className={`filter-dropdown-menu actions-dropdown-menu ${
                                dropdownAlignRight ? 'dropdown-right' : ''
                            }`}
                        >
                            {/* Toggle Switch Section */}
                            <div
                                className="actions-toggle-section"
                                onClick={e => e.stopPropagation()}
                            >
                                <span className="actions-toggle-label">
                                    Hide Virtual/Decommissioned Assets
                                </span>
                                <Toggle
                                    checked={hideDecommissionedAssets}
                                    onChange={handleHideAssetsToggle}
                                    icons={false}
                                />
                            </div>

                            {/* Actions */}
                            <MenuItem
                                eventKey="import"
                                className="filter-dropdown-item"
                                disabled={
                                    loading ||
                                    !canEditInstalls ||
                                    !isManageAssetView
                                }
                            >
                                <FontAwesomeIcon icon={['far', 'upload']} />
                                <span className="filter-option-text">
                                    {t('manageInventory:import')}
                                </span>
                            </MenuItem>
                            <MenuItem
                                eventKey="new-asset"
                                className="filter-dropdown-item"
                                disabled={
                                    loading ||
                                    !canEditInstalls ||
                                    !isManageAssetView
                                }
                            >
                                <FontAwesomeIcon icon={['far', 'plus']} />
                                <span className="filter-option-text">
                                    {t('manageInventory:newProduct')}
                                </span>
                            </MenuItem>
                            {getExportRequest && (
                                <ExportToExcelButton
                                    renderMode="menuItems"
                                    getExportRequest={getExportRequest}
                                />
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>
        </div>
    );
};

export default NewInventorySearch;
